import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Loader2, Plus } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { supabase } from '@/lib/supabase';
import { ComposeCareCommunication } from '@/pages/communicate/ComposeCareCommunication';
import {
  fetchParsedCareCommunicationBundle,
  useCareCommunicationsList,
  type CareCommunicationListItem,
} from '@/hooks/useCareCommunications';
import { useCareCommunicationReads } from '@/hooks/useCareCommunicationReads';
import { useDecryptCpr } from '@/hooks/usePatients';
import { newLetterPatientIdentifiers, type ReplyPatientIdentifiers } from '@/lib/medcom/replyPrefill';
import { Button } from '@/components/ui/Button';
import { CareCommunicationPriorityChip } from '@/components/medcom/CareCommunicationPriorityChip';
import { OrganizationName } from '@/components/medcom/OrganizationName';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { Skeleton } from '@/components/ui/Skeleton';
import { TABLE_CELL, TABLE_CELL_MUTED, TABLE_HEADER } from '@/components/ui/pageChrome';
import { cn } from '@/utils/cn';
import { unreadCountLabel } from '@/lib/medcom/careCommunicationRead';
import { careCommunicationThreadPath } from '@/lib/medcom/threadDisplay';
import { LetterCategory, LetterTopic } from '@/pages/communicate/LetterColumns';

interface Props {
  clinicId: string;
  patientId: string;
  firstName: string;
  lastName: string;
  hasCpr: boolean;
  canRevealCpr: boolean;
  /** Frame owns the header button; fetch/create logic stays here. */
  hideCreateButton?: boolean;
  onRegisterCreate?: (open: () => void) => void;
  onCountChange?: (count: number) => void;
  onComposingChange?: (composing: boolean) => void;
}

export function PatientCareCommunicationTab({
  clinicId,
  patientId,
  firstName,
  lastName,
  hasCpr,
  canRevealCpr,
  hideCreateButton = false,
  onRegisterCreate,
  onCountChange,
  onComposingChange,
}: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const decrypt = useDecryptCpr();
  const [creating, setCreating] = useState(false);
  const [opening, setOpening] = useState(false);
  const [initialPatient, setInitialPatient] = useState<{
    given: string;
    family: string;
    cpr: string;
    identifiers: ReplyPatientIdentifiers;
  } | null>(null);

  const links = useQuery({
    queryKey: ['patient-medcom-letters', clinicId, patientId],
    queryFn: async (): Promise<MedcomLetterLink[]> => {
      const { data, error } = await supabase
        .from('patient_medcom_letters' as never)
        .select('message_id, communication_identifier')
        .eq('clinic_id', clinicId)
        .eq('patient_id', patientId);
      if (error) throw error;
      return (data ?? []) as unknown as MedcomLetterLink[];
    },
  });

  const { data, isLoading, error } = useCareCommunicationsList(clinicId, {
    direction: 'in',
    status: 'all',
  });
  const { isUnread } = useCareCommunicationReads(clinicId, data?.items);

  const items = linkedLetters(data?.items ?? [], links.data ?? []);
  const unreadCount = items.filter(isUnread).length;
  const waiting = (isLoading && !data) || (links.isLoading && !links.data);

  const openCreate = async () => {
    if (opening) return;
    setOpening(true);
    try {
      let decryptedCpr: string | null = null;
      if (hasCpr && canRevealCpr) {
        try {
          const result = await decrypt.mutateAsync({
            patient_id: patientId,
            source: 'patient-medcom',
            purpose: 'care_communication',
          });
          decryptedCpr = result.cpr;
        } catch {
          toast.error(t('patients.medcom.cpr_failed'));
        }
      }
      const letterLinks = links.data ?? (await links.refetch()).data ?? [];
      const letters = await linkedLetterPatientIdentifiers(clinicId, letterLinks);
      const identifiers = newLetterPatientIdentifiers({ decryptedCpr, letters });
      setInitialPatient({
        given: firstName === lastName ? '' : firstName,
        family: lastName,
        cpr: identifiers.cpr ?? '',
        identifiers,
      });
      setCreating(true);
    } catch {
      toast.error(t('communicate.load_error'));
    } finally {
      setOpening(false);
    }
  };

  onRegisterCreate?.(() => {
    void openCreate();
  });

  useEffect(() => {
    onCountChange?.(items.length);
  }, [items.length, onCountChange]);

  useEffect(() => {
    onComposingChange?.(creating && Boolean(initialPatient));
  }, [creating, initialPatient, onComposingChange]);

  if (creating && initialPatient) {
    return (
      <ComposeCareCommunication
        mode="new"
        initialPatient={initialPatient}
        onClose={() => {
          setCreating(false);
          setInitialPatient(null);
        }}
      />
    );
  }

  return (
    <div>
      {hideCreateButton ? (
        unreadCount > 0 ? (
          <p className="mb-4 text-sm font-semibold text-ds-teal">{unreadCountLabel(unreadCount)}</p>
        ) : null
      ) : (
        <div className="mb-4 flex items-center justify-between">
          {unreadCount > 0 ? (
            <p className="text-sm font-semibold text-ds-teal">{unreadCountLabel(unreadCount)}</p>
          ) : (
            <span />
          )}
          <Button
            type="button"
            onClick={() => void openCreate()}
            disabled={opening || (links.isLoading && !links.data)}
          >
            {opening ? (
              <Loader2 className="mr-1 h-4 w-4 animate-spin" />
            ) : (
              <Plus className="mr-1 h-4 w-4" />
            )}
            {t('patients.medcom.new')}
          </Button>
        </div>
      )}

      {(error || links.error) && (
        <div className="mb-4">
          <ErrorAlert message={t('communicate.load_error')} />
        </div>
      )}

      {waiting ? (
        <div className="flex flex-col gap-s3" aria-busy>
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </div>
      ) : items.length === 0 ? (
        <p className="py-8 text-center text-sm text-ds-muted">
          {t('patients.medcom.incoming_empty')}
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-ds-line-strong">
            <thead className="bg-ds-sunk">
              <tr>
                <Th>{t('communicate.col_category')}</Th>
                <Th>{t('communicate.col_topic')}</Th>
                <Th>{t('communicate.col_type')}</Th>
                <Th>{t('communicate.col_sender')}</Th>
                <Th>{t('communicate.col_date')}</Th>
                <Th>{t('communicate.col_status')}</Th>
                <Th>{''}</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ds-line bg-ds-card">
              {items.map((item) => (
                <tr
                  key={item.id}
                  onClick={() => openLetter(navigate, item)}
                  className="cursor-pointer hover:bg-ds-row-hover"
                >
                  <td
                    className={cn(
                      TABLE_CELL,
                      'max-w-xs truncate px-3 py-4 first:pl-4',
                      isUnread(item) && 'font-semibold',
                    )}
                  >
                    <span className="inline-flex items-center gap-2">
                      {isUnread(item) && <UnreadBadge />}
                      <LetterCategory category={item.category} />
                      <CareCommunicationPriorityChip priority={item.priority} variant="compact" />
                    </span>
                  </td>
                  <td className={cn(TABLE_CELL_MUTED, 'max-w-xs truncate px-3 py-4')}>
                    <LetterTopic topic={item.topic} category={item.category} />
                  </td>
                  <td className={cn(TABLE_CELL_MUTED, 'whitespace-nowrap px-3 py-4')}>
                    {item.activityCode
                      ? t(`communicate.activity_${item.activityCode}`, { defaultValue: '—' })
                      : '—'}
                  </td>
                  <td className={cn(TABLE_CELL_MUTED, 'whitespace-nowrap px-3 py-4')}>
                    <OrganizationName name={item.senderOrganization} sorId={item.senderSor} fallback="—" />
                  </td>
                  <td className={cn(TABLE_CELL_MUTED, 'whitespace-nowrap px-3 py-4')}>
                    {formatMessageDate(item)}
                  </td>
                  <td className={cn(TABLE_CELL_MUTED, 'whitespace-nowrap px-3 py-4')}>
                    {t(`communicate.status_${item.status}`)}
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-right">
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={(event) => {
                        event.stopPropagation();
                        navigate(
                          `/medcom/letters/${encodeURIComponent(item.communicationIdentifier)}/reply?messageId=${encodeURIComponent(item.id)}`,
                        );
                      }}
                    >
                      {t('patients.medcom.reply')}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function UnreadBadge() {
  return (
    <span className="inline-flex items-center gap-1 rounded bg-ds-coral-badge px-1.5 py-0.5 text-[11px] font-semibold text-ds-on-danger">
      <span className="h-1.5 w-1.5 rounded-full bg-ds-on-danger" aria-hidden />
      Ulæst
    </span>
  );
}

export function PatientMedcomUnreadCount({
  clinicId,
  patientId,
}: {
  clinicId: string;
  patientId: string;
}) {
  const links = useQuery({
    queryKey: ['patient-medcom-letters', clinicId, patientId],
    queryFn: async (): Promise<MedcomLetterLink[]> => {
      const { data, error } = await supabase
        .from('patient_medcom_letters' as never)
        .select('message_id, communication_identifier')
        .eq('clinic_id', clinicId)
        .eq('patient_id', patientId);
      if (error) throw error;
      return (data ?? []) as unknown as MedcomLetterLink[];
    },
  });
  const { data } = useCareCommunicationsList(clinicId, { direction: 'in', status: 'all' });
  const { isUnread } = useCareCommunicationReads(clinicId, data?.items);
  const count = linkedLetters(data?.items ?? [], links.data ?? []).filter(isUnread).length;
  if (count === 0) return null;
  return (
    <span className="ml-1.5 inline-flex min-w-5 items-center justify-center rounded-full bg-ds-coral-badge px-1.5 text-[11px] font-semibold text-ds-on-danger">
      {count}
    </span>
  );
}

function linkedLetters(
  items: CareCommunicationListItem[],
  links: MedcomLetterLink[],
): CareCommunicationListItem[] {
  const linkedMessages = new Set(links.map((link) => link.message_id));
  const linkedCommunications = new Set(
    links
      .map((link) => link.communication_identifier)
      .filter((value): value is string => typeof value === 'string' && value.length > 0),
  );
  return items.filter(
    (item) => linkedMessages.has(item.id) || linkedCommunications.has(item.communicationIdentifier),
  );
}

function openLetter(navigate: ReturnType<typeof useNavigate>, item: CareCommunicationListItem) {
  navigate(careCommunicationThreadPath(item.communicationIdentifier, item.id));
}

function Th({ children }: { children: string }) {
  return (
    <th scope="col" className={cn(TABLE_HEADER, 'px-3 py-3.5 text-left first:pl-4')}>
      {children}
    </th>
  );
}

interface MedcomLetterLink {
  message_id: string;
  communication_identifier: string | null;
}

async function linkedLetterPatientIdentifiers(
  clinicId: string,
  links: MedcomLetterLink[],
): Promise<Array<{ cpr: string | null; xEcpr: string | null; dEcpr: string | null }>> {
  const messageIds = uniqueMessageIds(links);
  const results = await Promise.allSettled(
    messageIds.map((messageId) => fetchParsedCareCommunicationBundle(clinicId, messageId)),
  );
  const letters: Array<{ cpr: string | null; xEcpr: string | null; dEcpr: string | null }> = [];
  for (const result of results) {
    if (result.status !== 'fulfilled') continue;
    letters.push({
      cpr: result.value.cpr,
      xEcpr: result.value.xEcpr,
      dEcpr: result.value.dEcpr,
    });
  }
  return letters;
}

function uniqueMessageIds(links: MedcomLetterLink[]): string[] {
  const ids: string[] = [];
  const seen = new Set<string>();
  for (const link of links) {
    if (!link.message_id || seen.has(link.message_id)) continue;
    seen.add(link.message_id);
    ids.push(link.message_id);
  }
  return ids;
}

function formatMessageDate(item: CareCommunicationListItem): string {
  const iso = item.receivedAt ?? item.createdAt;
  if (!iso) return '—';
  return new Date(iso).toLocaleString('da-DK');
}
